import React from 'react';
import type { FunctionComponent } from 'react';
import type { StaffGet } from '@whizz/api-model';
import type { UseQueryResult } from 'react-query';
import { useI18n } from '@whizz/react-i18n';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { GlobalDashboardLayout } from '../../layouts';
import { ROUTES } from '../../util/vars';
import WelcomeCard from './WelcomeCard';
import WelcomeMoreInfoTip from './WelcomeMoreInfoTip';
import StaffTrainingIcon from '../../assets/welcome/staff-training.svg';
import NudgeWave from '../../assets/welcome/nudge-wave.svg';

type Props = {
  hasTraining: boolean;
  isNz: boolean;
  textAlign: string;
  staffQuery: UseQueryResult<StaffGet>;
  classSelector: React.ReactNode;
};

const DESCRIPTION_CLASS = 'text-gray-600';
const LONG_TITLE_LENGTH = 28;

const WelcomeStaff: FunctionComponent<Props> = ({ hasTraining, isNz, textAlign, staffQuery, classSelector }) => {
  const { translate } = useI18n();

  const hasNoAdmins = staffQuery.isSuccess && 0 === staffQuery.data?.teachers.filter((staff) => staff.isAdmin).length;
  const title = translate('pages.WELCOME.STAFF.HERO.TITLE');
  const isLongTitle = title.length > LONG_TITLE_LENGTH;

  const hero = (
    <div className={classNames('flex flex-col items-start justify-between gap-7', { 'lg:flex-row': !isLongTitle, 'xl:flex-row': isLongTitle })}>
      <div className={'flex-1'}>
        <div className={'font-extrabold uppercase mb-3 text-header text-xs tracking-widest'}>
          {translate('pages.WELCOME.STAFF.HERO.EYEBROW')}
        </div>
        <h1 className={'font-extrabold mb-3.5 text-black text-3xl tracking-tight leading-tight'}>
          {title}
        </h1>
        <p className={`${DESCRIPTION_CLASS} text-base leading-relaxed max-w-md ${textAlign}`}>
          {translate(hasTraining
            ? 'pages.WELCOME.STAFF.HERO.DESCRIPTION'
            : 'pages.WELCOME.STAFF.HERO.DESCRIPTION_NO_TRAINING')}
        </p>
      </div>
      <div
        className={classNames('flex-none w-full flex flex-col items-center gap-2 pt-3.5 px-2.5 pb-2.5 bg-header bg-opacity-20 border border-header border-opacity-30 rounded-lg', { 'lg:w-64': !isLongTitle, 'xl:w-64': isLongTitle })}
      >
        <img src={NudgeWave} alt={''} className={'object-contain w-44 h-16'} />
        <div className={'font-bold text-center text-black text-xs'}>{translate('pages.WELCOME.STAFF.HERO.CAPTION')}</div>
      </div>
    </div>
  );

  const openClassPicker = () => {
    const trigger = document.getElementById('academic_year_selector');
    if (trigger?.getAttribute('aria-pressed') !== 'true') {
      trigger?.click();
    }
  };

  const dropdownHint = (
    <div
      onClick={openClassPicker}
      className={`inline-block font-extrabold cursor-pointer border border-primary text-primary rounded-md py-2.5 px-3.5 text-lg leading-normal ${textAlign}`}
      data-cy={'welcome-dropdown-hint'}
    >
      ⌃ {translate('pages.WELCOME.STAFF.STEP_REPORTS.DROPDOWN_HINT')}
    </div>
  );

  const notListed = hasNoAdmins ? (
    <div className={`text-base font-semibold leading-relaxed ${DESCRIPTION_CLASS}`}>
      {translate('pages.WELCOME.COMMON.NO_ADMIN_MESSAGE')}{' '}
      <a href={`mailto:${translate('pages.WELCOME.COMMON.NO_ADMIN_EMAIL')}`} className={'text-primary'} >
        {translate('pages.WELCOME.COMMON.NO_ADMIN_EMAIL')}
      </a>
    </div>
  ) : (
    <div className={`text-base font-semibold leading-relaxed ${DESCRIPTION_CLASS}`}>
      {translate('pages.WELCOME.STAFF.STEP_REPORTS.NOT_LISTED_PREFIX')}{translate('pages.WELCOME.STAFF.STEP_REPORTS.NOT_LISTED_JOINER')}
      <span className={'text-primary'}>{translate('pages.WELCOME.COMMON.MENU')}</span>
      {' > '}
      <span className={'text-primary'}>{translate('pages.WELCOME.STAFF.STEP_REPORTS.ROSTER_MANAGEMENT_LABEL')}</span>.
    </div>
  );

  const stepBadge = (bgClassName: string, num: number) => (
    <span
      className={`flex-none w-8 h-8 rounded-full ${'bg-header' === bgClassName ? 'text-black' : 'text-white'} flex items-center justify-center font-extrabold text-xl ${bgClassName}`}
    >{num}</span>
  );

  const trainingStepCard = (
    <div
      className={'flex-1 flex flex-col gap-3 bg-header bg-opacity-20 border border-header border-opacity-30 rounded-lg p-6'}
      data-cy={'welcome-step-training'}
    >
      <div className={'flex items-center gap-3'}>
        {stepBadge('bg-header', 1)}
        <span className={'font-extrabold uppercase text-header text-xs tracking-wider'}>{translate('pages.WELCOME.STAFF.STEP_TRAINING.LABEL')}</span>
      </div>
      <h2 className={'font-extrabold text-xl text-black tracking-tight'}>{translate('pages.WELCOME.STAFF.STEP_TRAINING.TITLE')}</h2>
      <p className={`${DESCRIPTION_CLASS} text-base leading-relaxed ${textAlign}`}>{translate('pages.WELCOME.STAFF.STEP_TRAINING.DESCRIPTION')}</p>
      <div className={'flex items-start justify-between gap-4 mt-1'}>
        <Link
          to={ROUTES.training}
          className={'inline-flex items-center text-center text-black hover:opacity-90 rounded-md h-11 text-base font-bold mt-2 bg-header px-5 flex-shrink-0'}
          data-cy={'welcome-cta-training'}
        >
          {translate('pages.WELCOME.STAFF.STEP_TRAINING.CTA')}
        </Link>
        <img src={StaffTrainingIcon} alt={''} className={'flex-none object-contain w-auto h-28'} />
      </div>
    </div>
  );

  const reportsStepCard = (
    <div
      className={'flex-1 flex flex-col gap-3 bg-gray-50 border border-gray-200 rounded-lg p-6'}
      data-cy={'welcome-step-reports'}
    >
      <div className={'flex items-center gap-3'}>
        {hasTraining && stepBadge('bg-primary', 2)}
        <span className={'font-extrabold uppercase text-primary text-xs tracking-wider'}>{translate('pages.WELCOME.STAFF.STEP_REPORTS.LABEL')}</span>
      </div>
      <h2 className={'font-extrabold text-xl text-black tracking-tight'}>{translate('pages.WELCOME.STAFF.STEP_REPORTS.TITLE')}</h2>
      {dropdownHint}
      {notListed}
      <div className={'flex items-start gap-2 mt-2 pt-4 border-t border-dashed border-gray-200'}>
        <WelcomeMoreInfoTip isNz={isNz} />
      </div>
    </div>
  );

  return (
    <GlobalDashboardLayout disableContainer actionContent={classSelector}>
      <div className={'mx-auto max-w-4xl space-y-4'} data-cy={'welcome-page'}>
        <WelcomeCard variant={'staff'}>
          {hero}
        </WelcomeCard>

        <WelcomeCard variant={'staff'}>
          {hasTraining ? (
            <div className={'flex flex-col md:flex-row gap-6'}>
              {trainingStepCard}
              {reportsStepCard}
            </div>
          ) : reportsStepCard}
        </WelcomeCard>
      </div>
    </GlobalDashboardLayout>
  );
};

export default WelcomeStaff;
